import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Section } from 'src/components/Section';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import {
    useConsumers,
    useMessages,
    useCurrentOrganization,
} from 'src/view/hooks';
import { useConsumer } from 'src/view/hooks/consumer';
import { useCreators } from 'src/view/hooks/creator';
import { useNextOfKins } from 'src/view/hooks/nextOfKin';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { MessagesList } from 'src/view/screens/Messages/MessagesList';
import { useOrganizationGroupsById } from 'src/view/hooks/useOrganizationGroupsById';
import {
    buildLeafGroupLabelsById,
    buildParentGroupLabelsByConsumerId,
} from 'src/utils/consumerLabelUtils';
import { MessageAttachmentsField } from 'src/view/screens/Messages/MessageAttachmentsField';
import {
    MessageAttachmentConversionError,
    uploadMessageAttachments,
} from 'src/view/screens/Messages/messageAttachmentUpload';
import { BackIcon, PlusIcon } from 'src/components/Icons/icons';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { ChipSelect } from 'src/components/ChipSelect/ChipSelect';
import { OrganizationType } from 'src/domain/organization';
import { badgeVariantToClassName } from 'src/utils/badgeUtils';
import {
    MessageReadFilter,
    MessageReadFilterTabs,
} from 'src/view/screens/Messages/MessageReadFilterTabs';

const {
    cradle: { munduClient, storageService },
} = container;

const DEFAULT_PAGE_SIZE = 5;
const ALL_RELATIVES_OPTION_ID = 'all-relatives';

const getProfileLabel = (
    profile: { firstName?: string; lastName?: string } | undefined,
    fallback: string,
) => {
    const label = `${profile?.firstName ?? ''} ${
        profile?.lastName ?? ''
    }`.trim();

    return label || fallback;
};

type ConsumerCreateMessageFormProps = {
    organizationId: string;
    consumerId: string;
    onSuccess: () => void;
};

const ConsumerCreateMessageForm = ({
    organizationId,
    consumerId,
    onSuccess,
}: ConsumerCreateMessageFormProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const { organization } = useCurrentOrganization();
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedNextOfKinIds, setSelectedNextOfKinIds] = useState<string[]>(
        [],
    );

    const isNursingHome = organization?.type === OrganizationType.nursingHome;
    const { data: nextOfKins } = useNextOfKins({
        consumerId,
    });

    const resolveBadgeLabel = useCallback(
        (nextOfKin: { isMainContact?: boolean; isGuardian?: boolean }) =>
            nextOfKin.isMainContact
                ? t('modules.messages.form.recipientsBadgeMainContact')
                : nextOfKin.isGuardian
                ? t('modules.messages.form.recipientsBadgeGuardian')
                : undefined,
        [t],
    );

    const resolveBadgeShortLabel = useCallback(
        (nextOfKin: { isMainContact?: boolean; isGuardian?: boolean }) =>
            nextOfKin.isMainContact
                ? t('modules.messages.form.recipientsBadgeMainContactShort')
                : nextOfKin.isGuardian
                ? t('modules.messages.form.recipientsBadgeGuardianShort')
                : undefined,
        [t],
    );

    const familyOptions = useMemo(
        () =>
            (nextOfKins ?? [])
                .filter(nok => nok.relation === 'family')
                .map(nok => ({
                    id: nok.id,
                    label:
                        `${nok.profile.firstName} ${nok.profile.lastName}`.trim() ||
                        nok.id,
                    badgeVariant: nok.isMainContact
                        ? ('main' as const)
                        : nok.isGuardian
                        ? ('guardian' as const)
                        : undefined,
                    badgeLabel: resolveBadgeLabel(nok),
                    badgeShortLabel: resolveBadgeShortLabel(nok),
                })),
        [nextOfKins, resolveBadgeLabel, resolveBadgeShortLabel],
    );

    const nonFamilyOptions = useMemo(
        () =>
            (nextOfKins ?? [])
                .filter(nok => nok.relation !== 'family')
                .map(nok => ({
                    id: nok.id,
                    label:
                        `${nok.profile.firstName} ${nok.profile.lastName}`.trim() ||
                        nok.id,
                    badgeVariant: nok.isMainContact
                        ? ('main' as const)
                        : nok.isGuardian
                        ? ('guardian' as const)
                        : undefined,
                    badgeLabel: resolveBadgeLabel(nok),
                    badgeShortLabel: resolveBadgeShortLabel(nok),
                })),
        [nextOfKins, resolveBadgeLabel, resolveBadgeShortLabel],
    );

    const familyNextOfKinIds = useMemo(
        () => familyOptions.map(option => option.id),
        [familyOptions],
    );

    const allRelativesSelected = useMemo(
        () =>
            familyNextOfKinIds.length > 0 &&
            familyNextOfKinIds.every(id => selectedNextOfKinIds.includes(id)),
        [familyNextOfKinIds, selectedNextOfKinIds],
    );

    const allRelativesIndicators = useMemo(() => {
        const hasMain = familyOptions.some(
            option => option.badgeVariant === 'main',
        );
        const hasGuardian = familyOptions.some(
            option => option.badgeVariant === 'guardian',
        );

        return [
            ...(hasMain ? (['main'] as const) : []),
            ...(hasGuardian ? (['guardian'] as const) : []),
        ];
    }, [familyOptions]);

    const allRelativesShortLabels = useMemo(
        () => ({
            main: t('modules.messages.form.recipientsBadgeMainContactShort'),
            guardian: t('modules.messages.form.recipientsBadgeGuardianShort'),
        }),
        [t],
    );

    const nextOfKinOptions = useMemo(() => {
        if (!familyOptions.length) {
            return nonFamilyOptions;
        }

        return [
            {
                id: ALL_RELATIVES_OPTION_ID,
                label: t('modules.messages.form.recipientsAllRelatives'),
                badgeIndicators: allRelativesIndicators.length
                    ? allRelativesIndicators
                    : undefined,
                badgeShortLabels: allRelativesShortLabels,
                options: familyOptions,
            },
            ...nonFamilyOptions,
        ];
    }, [
        allRelativesIndicators,
        allRelativesShortLabels,
        familyOptions,
        nonFamilyOptions,
        t,
    ]);

    const isValid = subject.trim().length >= 1;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!isValid || isSubmitting) {
            return;
        }

        if (isNursingHome && selectedNextOfKinIds.length === 0) {
            notify(t('modules.messages.form.recipientsRequired'), true);
            return;
        }

        setIsSubmitting(true);

        try {
            let attachmentsPayload:
                | NonNullable<
                      Parameters<
                          typeof munduClient.createMessage
                      >[0]['attachments']
                  >
                | undefined;
            let attachmentsContainerName: string | undefined;

            if (attachments.length) {
                const sas = await munduClient.getMessageMediaSas();
                attachmentsContainerName = sas.containerName;
                attachmentsPayload = await uploadMessageAttachments(
                    attachments,
                    sas,
                    storageService,
                );
            }

            await munduClient.createMessage({
                organizationId,
                subject: subject.trim(),
                body: body.trim(),
                receiverId: consumerId,
                sendToAll: false,
                groupIds: undefined,
                consumerIds: isNursingHome ? undefined : [consumerId],
                nextOfKinIds: isNursingHome ? selectedNextOfKinIds : undefined,
                commentsEnabled: true,
                attachments: attachmentsPayload,
                attachmentsContainerName,
            });

            notify(t('modules.messages.notifications.created'));
            setSelectedNextOfKinIds([]);
            setSubject('');
            setBody('');
            setAttachments([]);
            onSuccess();
        } catch (error) {
            if (error instanceof MessageAttachmentConversionError) {
                notify(
                    t('modules.messages.form.attachmentConversionFailed'),
                    true,
                );
            } else if (error instanceof Error) {
                notify(error.message, true);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
                {isNursingHome && (
                    <ChipSelect
                        label={t('modules.messages.form.recipientsLabel')}
                        placeholder={t(
                            'modules.messages.form.recipientsPlaceholder',
                        )}
                        options={nextOfKinOptions}
                        selectedIds={
                            allRelativesSelected
                                ? [
                                      ALL_RELATIVES_OPTION_ID,
                                      ...selectedNextOfKinIds,
                                  ]
                                : selectedNextOfKinIds
                        }
                        displayedSelectedIds={
                            allRelativesSelected
                                ? [
                                      ALL_RELATIVES_OPTION_ID,
                                      ...selectedNextOfKinIds.filter(
                                          id =>
                                              !familyNextOfKinIds.includes(id),
                                      ),
                                  ]
                                : selectedNextOfKinIds
                        }
                        onChange={nextIds => {
                            const hasAllRelatives = nextIds.includes(
                                ALL_RELATIVES_OPTION_ID,
                            );
                            const cleaned = nextIds.filter(
                                id => id !== ALL_RELATIVES_OPTION_ID,
                            );

                            const withoutAllRelatives =
                                !hasAllRelatives && allRelativesSelected
                                    ? cleaned.filter(
                                          id =>
                                              !familyNextOfKinIds.includes(id),
                                      )
                                    : cleaned;

                            const nextSelection = hasAllRelatives
                                ? Array.from(
                                      new Set([
                                          ...withoutAllRelatives,
                                          ...familyNextOfKinIds,
                                      ]),
                                  )
                                : withoutAllRelatives;

                            setSelectedNextOfKinIds(nextSelection);
                        }}
                    />
                )}
            </div>
            <div>
                <label className="mb-1 block text-sm font-medium text-woodyBrown">
                    {t('modules.messages.form.subjectLabel')}
                </label>
                <input
                    className="text-input w-full"
                    value={subject}
                    onChange={event => setSubject(event.target.value)}
                />
            </div>
            <div>
                <label className="mb-1 block text-sm font-medium text-woodyBrown">
                    {t('modules.messages.form.bodyLabel')}
                </label>
                <textarea
                    className="text-input w-full min-h-[160px]"
                    value={body}
                    onChange={event => setBody(event.target.value)}
                />
            </div>
            <MessageAttachmentsField
                attachments={attachments}
                onChange={setAttachments}
                disabled={isSubmitting}
            />
            <button
                type="submit"
                className="primary-btn-small self-end disabled:opacity-50"
                disabled={!isValid || isSubmitting}
            >
                {t('modules.messages.form.submit')}
            </button>
        </form>
    );
};

type ConsumerMessagesTabProps = {
    organizationId: string;
    consumerId: string;
};

export const ConsumerMessagesTab = ({
    organizationId,
    consumerId,
}: ConsumerMessagesTabProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const [pageIndex, setPageIndex] = useState(1);
    const [isCreatingOnMobile, setIsCreatingOnMobile] = useState(false);
    const [messageReadFilter, setMessageReadFilter] =
        useState<MessageReadFilter>('all');

    const { organization } = useCurrentOrganization();
    const { consumer } = useConsumer(consumerId);
    const { consumers } = useConsumers({ organizationId });
    const { creators } = useCreators();
    const { data: nextOfKins } = useNextOfKins({ organizationId });

    const { messages, isLoading, isError, mutate, isValidating, pageInfo } =
        useMessages({
            organizationId,
            consumerId,
            strict: true,
            readStatus: messageReadFilter === 'unread' ? 'unread' : 'all',
            sentByMe: messageReadFilter === 'sentByMe',
            directToConsumer: messageReadFilter === 'personal',
            limit: DEFAULT_PAGE_SIZE,
            pageIndex,
        });

    const { groupsById } = useOrganizationGroupsById(organizationId);

    useEffect(() => {
        if (isError instanceof Error) {
            notify(isError.message, true);
        }
    }, [isError, notify]);

    const pageSize = pageInfo?.pageSize ?? DEFAULT_PAGE_SIZE;
    const totalMessages = pageInfo?.total ?? messages.length;
    const derivedPageCount = totalMessages
        ? Math.max(1, Math.ceil(totalMessages / pageSize))
        : 0;

    useEffect(() => {
        if (derivedPageCount > 0 && pageIndex > derivedPageCount) {
            setPageIndex(derivedPageCount);
        }
    }, [derivedPageCount, pageIndex]);

    useEffect(() => {
        setPageIndex(1);
    }, [messageReadFilter]);

    const pagedMessages = messages;

    const recipientsById = useMemo(() => {
        const consumerLabel = consumer
            ? getProfileLabel(consumer.profile, consumer.id)
            : consumerId;

        const consumerEntries: [string, string][] = [
            [consumerId, consumerLabel],
        ];

        const nokEntries: [string, string][] =
            nextOfKins?.map(nok => [
                nok.id,
                getProfileLabel(nok.profile, nok.id),
            ]) ?? [];

        const creatorEntries: [string, string][] = creators.map(creator => [
            creator.id,
            getProfileLabel(creator.profile, creator.email ?? creator.id),
        ]);

        const organizationEntries: [string, string][] = organization
            ? [
                  [
                      organization.id,
                      getProfileLabel(
                          organization.profile,
                          organization.organizationDisplayName ||
                              organization.organizationName ||
                              organization.id,
                      ),
                  ],
              ]
            : [];

        const signedInUserEntries: [string, string][] = user
            ? [[user.id, getProfileLabel(user.profile, user.id)]]
            : [];

        return new Map<string, string>([
            ...consumerEntries,
            ...nokEntries,
            ...creatorEntries,
            ...organizationEntries,
            ...signedInUserEntries,
        ]);
    }, [consumer, consumerId, creators, nextOfKins, organization, user]);

    const leafGroupLabelsById = useMemo(
        () => buildLeafGroupLabelsById(consumer ? [consumer] : undefined),
        [consumer],
    );

    const parentGroupLabelsByConsumerId = useMemo(
        () =>
            buildParentGroupLabelsByConsumerId(
                consumer ? [consumer] : undefined,
                organization?.groups,
            ),
        [consumer, organization?.groups],
    );
    const senderBadgesByUserId = useMemo(() => {
        const entries =
            nextOfKins
                ?.map(nok => {
                    const badgeVariant = nok.isMainContact
                        ? ('main' as const)
                        : nok.isGuardian
                        ? ('guardian' as const)
                        : undefined;

                    if (!badgeVariant) {
                        return undefined;
                    }

                    return [
                        nok.id,
                        {
                            label: nok.isMainContact
                                ? t(
                                      'modules.messages.form.recipientsBadgeMainContact',
                                  )
                                : t(
                                      'modules.messages.form.recipientsBadgeGuardian',
                                  ),
                            className: badgeVariantToClassName[badgeVariant],
                        },
                    ] as const;
                })
                .filter(
                    (
                        entry,
                    ): entry is readonly [
                        string,
                        { label: string; className: string },
                    ] => Boolean(entry),
                ) ?? [];

        return new Map(entries);
    }, [nextOfKins, t]);

    const handleMessageCreated = async () => {
        setPageIndex(1);
        await mutate();
        setIsCreatingOnMobile(false);
    };

    const handleMessageDeleted = async () => {
        await mutate();
    };

    return (
        <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,1fr)]">
            <div
                className={
                    isCreatingOnMobile ? 'hidden min-w-0 lg:block' : 'min-w-0'
                }
            >
                <Section
                    title={t('modules.messages.listTitle')}
                    className="min-w-0 gap-2.5"
                    action={
                        <button
                            type="button"
                            className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-1 lg:hidden"
                            onClick={() => setIsCreatingOnMobile(true)}
                        >
                            <TextIcon SVG={PlusIcon} />
                            {t('modules.messages.createTitle')}
                        </button>
                    }
                >
                    <MessageReadFilterTabs
                        residentView
                        value={messageReadFilter}
                        onChange={setMessageReadFilter}
                    />
                    <MessagesList
                        messages={pagedMessages}
                        isLoading={isLoading}
                        isRefreshing={isValidating}
                        consumers={consumers}
                        organizationGroups={organization?.groups}
                        groupsById={groupsById}
                        leafGroupLabelsById={leafGroupLabelsById}
                        parentGroupLabelsByConsumerId={
                            parentGroupLabelsByConsumerId
                        }
                        recipientsById={recipientsById}
                        senderBadgesByUserId={senderBadgesByUserId}
                        pageCount={derivedPageCount}
                        currentPage={pageIndex}
                        onPageChange={setPageIndex}
                        onRefreshList={handleMessageDeleted}
                        refreshAfterVisibleRead={messageReadFilter !== 'unread'}
                        showCommentToggle
                    />
                </Section>
            </div>
            <div
                className={
                    isCreatingOnMobile ? 'min-w-0' : 'hidden min-w-0 lg:block'
                }
            >
                <Section
                    title={t('modules.messages.createTitle')}
                    className="min-w-0"
                    action={
                        <button
                            type="button"
                            className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-1 lg:hidden"
                            onClick={() => setIsCreatingOnMobile(false)}
                        >
                            <TextIcon SVG={BackIcon} />
                            {t('modules.messages.backToList')}
                        </button>
                    }
                >
                    <ConsumerCreateMessageForm
                        organizationId={organizationId}
                        consumerId={consumerId}
                        onSuccess={handleMessageCreated}
                    />
                </Section>
            </div>
        </div>
    );
};
